import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  Divider,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UpdateIcon from '@mui/icons-material/Update';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TitleIcon from '@mui/icons-material/Title';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InfoIcon from '@mui/icons-material/Info';
import { generateResponse, AIJsonResponse } from '../../services/geminiService';
import {
  saveDraft,
  loadDraft,
  DraftMessage,
  DraftAgent,
} from '../../services/draftService';

import { useDocumentContext } from '../../context/DocumentContext';
import { useAgentContext } from '../../context/AgentContext';
import { Agent } from '../../services/api';
import {
  prepareContentForAI,
  markdownToHtml,
  htmlToMarkdown,
} from '../../utils/markdownConverter';
import TOCModal from './TOCModal';
import './ChatSection.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { debounce } from '../../utils/debounce';
import {
  NAVIGATION_INTENT_EVENT,
  NavigationIntentDetail,
} from '../Layout/Navbar';
import { useAuthContext } from '../../context/AuthContext';

// Define message interface with type and additional data
interface Message {
  id: number;
  text: string;
  isUser: boolean;
  agentType?: string;
  documentTitle?: string;
  documentContent?: string;
  tableOfContents?: string;
}

// Define expandedState type
interface ExpandedState {
  [messageId: number]: boolean;
}

// Extend Window interface to include custom functions
declare global {
  interface Window {
    getLatestTOC?: () => string;
    getLatestDocumentContent?: () => string;
  }
}

// Utility function to generate a unique id
const generateUniqueId = (): number => {
  return Math.floor(Math.random() * 1000000000);
};

// Helper function to get icon for agent type
const getAgentIcon = (agentType: string) => {
  switch (agentType) {
    case 'natural_agent':
      return <TitleIcon fontSize="small" />;
    case 'toc_agent':
      return <FormatListBulletedIcon fontSize="small" />;
    case 'document_agent':
      return <MenuBookIcon fontSize="small" />;
    default:
      return <SmartToyIcon fontSize="small" />;
  }
};

interface SaveAndNavigateEventDetail {
  targetPath: string;
  isLogout?: boolean;
}

// Add this interface for better type safety
interface NavigationState {
  isNavigating: boolean;
  targetPath: string;
  isLogout?: boolean;
}

const ChatSection: React.FC = () => {
  const {
    markdownContent,
    updateMarkdownContent,
    title,
    updateTitle,
    content,
  } = useDocumentContext();
  const { selectedAgent, loadingAgent, agentError, fetchAgentById } =
    useAgentContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthContext();

  // Simplified navigation state - single state object instead of multiple
  const [navigationState, setNavigationState] =
    useState<NavigationState | null>(null);

  // Add ref to track draft loading state
  const draftLoadedRef = useRef(false);
  const initialLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNewChatRef = useRef(true); // Add ref to track if this is a new chat

  // State for chat messages and user input
  const [messages, setMessages] = useState<Message[]>([
    {
      id: generateUniqueId(),
      text: 'Hello! I can help you create a document. What would you like to write about?',
      isUser: false,
      agentType: 'natural_agent',
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // State for document preview and TOC modal
  const [updatingDocId, setUpdatingDocId] = useState<number | null>(null);
  const [expandedDocs, setExpandedDocs] = useState<ExpandedState>({});
  const [tocDialogOpen, setTocDialogOpen] = useState(false);
  const [currentToc, setCurrentToc] = useState<string>('');
  const [currentTitle, setCurrentTitle] = useState<string>('');

  // Draft-related state
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [saveDraftDialogOpen, setSaveDraftDialogOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState<string>('');
  const [draftDescription, setDraftDescription] = useState<string>('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  // Auto-save and change tracking state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedState, setLastSavedState] = useState<{
    messages: Message[];
    content: string;
    markdownContent: string;
    title: string;
    toc: string;
  } | null>(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Refs to track current document state - SIMPLIFIED
  const tocRef = useRef<string>('');
  const titleRef = useRef<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Add auto-scroll functionality
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  // Auto-save timer management
  useEffect(() => {
    // Clear existing timer
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    // Set up new timer if there are unsaved changes (with or without existing draft)
    if (hasUnsavedChanges && messages.length > 1) {
      const timer = setTimeout(
        () => {
          handleAutoSave();
        },
        15 * 60 * 1000
      ); // 15 minutes in milliseconds

      setAutoSaveTimer(timer);
    }

    // Cleanup function
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [hasUnsavedChanges, currentDraftId, messages.length]);

  // Check for agent ID in URL and load agent, or draft ID and load draft
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const agentId = searchParams.get('agent');
    const draftId = searchParams.get('draft');

    // If loading a draft, handle that first (drafts contain their own agent info)
    // Only load if we haven't already loaded this draft
    if (draftId && !isLoadingDraft && draftId !== currentDraftId) {
      isNewChatRef.current = false;
      loadDraftData(draftId);
      return;
    }

    // If no agent ID and no draft ID, redirect to agents page
    if (!agentId && !draftId) {
      navigate('/agents');
      return;
    }

    // For new chats (with agent but no draft), enable change tracking immediately
    if (agentId && !draftId) {
      isNewChatRef.current = true;
      draftLoadedRef.current = true; // Enable change tracking for new chats
    }

    // If we have an agent ID but no selected agent, load the agent
    // Only run this when we don't have a draft (drafts handle their own agents)
    if (
      agentId &&
      !draftId &&
      agentId !== selectedAgent?._id &&
      !loadingAgent
    ) {
      fetchAgentById(agentId).then((agent: Agent | null) => {
        if (!agent) {
          // Agent not found, redirect to agents page
          navigate('/agents');
        }
      });
    }
  }, [
    location.search,
    fetchAgentById,
    navigate,
    isLoadingDraft,
    loadingAgent,
    currentDraftId,
    selectedAgent?._id,
  ]);

  // Debounced change tracking to reduce performance impact
  const debouncedChangeTracking = useCallback(
    debounce(() => {
      // Skip change tracking if draft is still loading and this is not a new chat
      if (!draftLoadedRef.current && !isNewChatRef.current) {
        return;
      }

      if (!lastSavedState) {
        // For new chats or no saved state, consider changes if there's meaningful content
        setHasUnsavedChanges(
          messages.length > 1 ||
            (title !== 'Untitled Document' && title.trim().length > 0) ||
            !!content ||
            !!markdownContent ||
            !!tocRef.current
        );
        return;
      }

      // Check if current state differs from last saved state
      // Use consistent content source (same as saving)
      const currentMarkdownContent = getDocumentContentForSaving();
      const currentState = {
        messages,
        content: content || '',
        markdownContent: currentMarkdownContent,
        title,
        toc: tocRef.current || '',
      };

      const hasChanges =
        JSON.stringify(currentState.messages) !==
          JSON.stringify(lastSavedState.messages) ||
        currentState.content !== lastSavedState.content ||
        currentState.markdownContent !== lastSavedState.markdownContent ||
        currentState.title !== lastSavedState.title ||
        currentState.toc !== lastSavedState.toc;

      setHasUnsavedChanges(hasChanges);
    }, 500),
    [messages, content, markdownContent, title, tocRef.current, lastSavedState]
  );

  // Track changes to determine if save button should be enabled - DEBOUNCED
  useEffect(() => {
    debouncedChangeTracking();
  }, [debouncedChangeTracking]);

  // Add global function to get latest TOC content
  useEffect(() => {
    window.getLatestTOC = () => tocRef.current || '';

    return () => {
      delete window.getLatestTOC;
    };
  }, []);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, []);

  // Update welcome message when agent changes (only for new chats, not drafts)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const draftId = searchParams.get('draft');

    // Only show welcome message for new chats with agents, not when loading drafts
    if (selectedAgent && !draftId && !isLoadingDraft) {
      setMessages([
        {
          id: generateUniqueId(),
          text: `Hallo! Ich bin dein KI-Dokumentenassistent. Ich helfe dir, ein Dokument zu erstellen${selectedAgent.siteUrl ? ` unter Verwendung verifizierter Informationen von ${selectedAgent.siteUrl}` : ''}.

Deine Unterhaltung wird automatisch alle 15 Minuten als Entwurf gespeichert.

Wie möchtest du dein Dokument betiteln?`,
          isUser: false,
          agentType: 'natural_agent',
        },
      ]);
    }
  }, [selectedAgent, location.search, isLoadingDraft]);

  // Helper function to show snackbar
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  // Helper function to close snackbar
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load draft data from storage
  const loadDraftData = async (draftId: string) => {
    try {
      isNewChatRef.current = false; // This is not a new chat
      draftLoadedRef.current = false; // Reset draft loaded state
      setIsLoadingDraft(true);
      const draft = await loadDraft(draftId);

      // Convert DraftMessage[] to Message[]
      const convertedMessages: Message[] = draft.messages.map((msg) => ({
        id: msg.id,
        text: msg.text,
        isUser: msg.isUser,
        agentType: msg.agentType,
        documentTitle: msg.documentTitle,
        documentContent: msg.documentContent,
        tableOfContents: msg.tableOfContents,
      }));

      // Update all state
      setMessages(convertedMessages);
      setDraftTitle(draft?.title || '');
      setCurrentDraftId(draft.id);

      // Update document context
      if (draft.documentTitle) {
        updateTitle(draft.documentTitle);
      }
      if (draft.markdownContent) {
        updateMarkdownContent(draft.markdownContent);
      }

      // Update refs
      tocRef.current = draft.tableOfContents || '';
      titleRef.current = draft.documentTitle || '';

      // Update local state
      setCurrentToc(draft.tableOfContents || '');
      setCurrentTitle(draft.documentTitle || '');

      // Load agent from draft if available and not already loaded
      if (draft.selectedAgent && draft.selectedAgent.id && !selectedAgent) {
        const agent = await fetchAgentById(draft.selectedAgent.id);
        if (!agent) {
          console.warn('Agent from draft not found:', draft.selectedAgent.id);
        }
      }

      // Update saved state tracking after loading - ensure consistency
      const loadedMarkdownContent =
        draft.markdownContent || draft.documentContent || '';
      setLastSavedState({
        messages: convertedMessages,
        content: draft.documentContent || '',
        markdownContent: loadedMarkdownContent,
        title: draft.documentTitle || '',
        toc: draft.tableOfContents || '',
      });
      setHasUnsavedChanges(false);

      // Set draft as loaded after a short delay to allow all state updates to complete
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current);
      }
      initialLoadTimeoutRef.current = setTimeout(() => {
        draftLoadedRef.current = true;
        initialLoadTimeoutRef.current = null;
      }, 1000); // Wait 1 second after loading to start tracking changes

      showSnackbar(`Draft "${draft.title}" loaded successfully`, 'success');
    } catch (error) {
      console.error('Error loading draft:', error);
      showSnackbar('Failed to load draft', 'error');
      draftLoadedRef.current = true; // Enable change tracking on error
      isNewChatRef.current = true; // Reset to new chat on error
    } finally {
      setIsLoadingDraft(false);
    }
  };

  // Handle opening save draft dialog
  const handleOpenSaveDraftDialog = () => {
    const defaultTitle =
      title && title !== 'Untitled Document'
        ? draftTitle || title
        : `Chat Draft - ${new Date().toLocaleDateString()}`;

    setDraftTitle(defaultTitle);
    setDraftDescription('');
    setSaveDraftDialogOpen(true);
  };

  // Helper function to get consistent document content for saving - SIMPLIFIED
  const getDocumentContentForSaving = (): string => {
    // Use the global function from DocumentEditor if available
    if (window.getLatestDocumentContent) {
      return window.getLatestDocumentContent();
    }

    // Fallback to context content
    if (markdownContent) {
      return markdownContent;
    } else if (content) {
      try {
        return htmlToMarkdown(content);
      } catch (error) {
        console.error('Error converting HTML to markdown for saving:', error);
        return content; // Fallback to HTML
      }
    }
    return '';
  };

  // Helper function to update last saved state
  const updateLastSavedState = () => {
    const documentContentForSaving = getDocumentContentForSaving();
    setLastSavedState({
      messages: [...messages],
      content: content || '',
      markdownContent: documentContentForSaving, // Use consistent content
      title,
      toc: tocRef.current || '',
    });
    setHasUnsavedChanges(false);
  };

  // Auto-save function - IMPROVED with consistent content handling
  const handleAutoSave = async () => {
    if (!hasUnsavedChanges || messages.length <= 1) return;

    try {
      setIsSavingDraft(true);

      // Get consistent document content
      const documentContentForSaving = getDocumentContentForSaving();

      // Convert Message[] to DraftMessage[]
      const draftMessages: DraftMessage[] = messages.map((msg) => ({
        id: msg.id,
        text: msg.text,
        isUser: msg.isUser,
        agentType: msg.agentType,
        documentTitle: msg.documentTitle,
        documentContent: msg.documentContent,
        tableOfContents: msg.tableOfContents,
      }));

      // Convert agent to DraftAgent if available
      const draftAgent: DraftAgent | undefined = selectedAgent
        ? {
            id: selectedAgent._id || 'unknown',
            name: selectedAgent.name,
            siteUrl: selectedAgent.siteUrl,
            description: selectedAgent.description,
          }
        : undefined;

      // Generate default draft title if no current draft exists
      let autoSaveTitle = draftTitle;
      if (!currentDraftId) {
        if (titleRef.current && titleRef.current !== 'Untitled Document') {
          autoSaveTitle = titleRef.current;
        } else {
          autoSaveTitle =
            draftTitle ||
            title ||
            `Auto-saved Chat - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
        }
        setDraftTitle(autoSaveTitle);
      }

      // Save with consistent content - both documentContent and markdownContent use the same source
      const savedDraftId = await saveDraft(
        autoSaveTitle,
        draftMessages,
        documentContentForSaving, // Consistent content
        documentContentForSaving, // Same content for markdownContent
        titleRef.current,
        tocRef.current,
        draftAgent,
        '', // No description for auto-save
        currentDraftId || undefined
      );

      // Set the draft ID if this was the first auto-save
      if (!currentDraftId) {
        setCurrentDraftId(savedDraftId);
      }

      updateLastSavedState();
      showSnackbar(
        currentDraftId
          ? 'Draft auto-saved successfully'
          : 'Draft auto-created and saved successfully',
        'info'
      );
    } catch (error) {
      console.error('Error auto-saving draft:', error);
      showSnackbar('Auto-save failed', 'error');
    } finally {
      setIsSavingDraft(false);
    }
  };

  // NAVIGATION GUARD IMPLEMENTATION
  // ================================
  // This implements a navigation guard that prevents users from losing unsaved changes
  // when navigating away from the chat route. The flow is:
  //
  // 1. User clicks navigation button in Navbar
  // 2. Navbar dispatches NAVIGATION_INTENT_EVENT with target path and callback
  // 3. ChatSection checks for unsaved changes and responds via callback
  // 4. If unsaved changes exist, Navbar shows confirmation modal
  // 5. User clicks "Save & Continue" which dispatches saveAndNavigate event
  // 6. ChatSection saves the draft and navigates immediately after success
  // 7. All navigation state is properly cleaned up on completion or error
  //
  // Key improvements from previous implementation:
  // - Promise-based saving instead of timer-based navigation
  // - Simplified state management with single navigationState object
  // - Proper error handling and state cleanup
  // - Eliminated race conditions and page refresh bugs

  // Update the save and navigate handler
  useEffect(() => {
    const handleNavigationIntent = (event: Event) => {
      const customEvent = event as CustomEvent<NavigationIntentDetail>;
      const { targetPath, callback } = customEvent.detail;

      // Check if we have unsaved changes
      if (hasUnsavedChanges && messages.length > 1) {
        callback(false); // Tell Navbar to show confirmation modal
      } else {
        callback(true); // Tell Navbar to proceed with navigation
      }
    };

    const handleSaveAndNavigate = async (event: Event) => {
      const customEvent = event as CustomEvent<SaveAndNavigateEventDetail>;
      const { targetPath, isLogout } = customEvent.detail;

      try {
        // Set navigation state
        setNavigationState({ isNavigating: true, targetPath, isLogout });

        // Save the draft and wait for completion
        await handleSaveDraft();

        // Notify Navbar to close the modal
        window.dispatchEvent(new CustomEvent('closeNavigationModal'));
        showSnackbar('Draft saved successfully. Redirecting...', 'success');

        // Navigate immediately after successful save
        if (isLogout) {
          logout();
        }
        navigate(targetPath);
      } catch (error) {
        console.error('Error saving before navigation:', error);
        showSnackbar('Failed to save draft', 'error');
      } finally {
        // Reset navigation state
        setNavigationState(null);
      }
    };

    // Add event listeners
    window.addEventListener(NAVIGATION_INTENT_EVENT, handleNavigationIntent);
    window.addEventListener('saveAndNavigate', handleSaveAndNavigate);

    // Cleanup
    return () => {
      window.removeEventListener(
        NAVIGATION_INTENT_EVENT,
        handleNavigationIntent
      );
      window.removeEventListener('saveAndNavigate', handleSaveAndNavigate);
    };
  }, [hasUnsavedChanges, messages.length, navigate, logout]);

  // Update handleSaveDraft to return a promise
  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      // Emit saving state
      window.dispatchEvent(
        new CustomEvent('savingStateChange', { detail: { saving: true } })
      );

      // Get consistent document content
      const documentContentForSaving = getDocumentContentForSaving();

      // Convert Message[] to DraftMessage[]
      const draftMessages: DraftMessage[] = messages.map((msg) => ({
        id: msg.id,
        text: msg.text,
        isUser: msg.isUser,
        agentType: msg.agentType,
        documentTitle: msg.documentTitle,
        documentContent: msg.documentContent,
        tableOfContents: msg.tableOfContents,
      }));

      // Convert agent to DraftAgent if available
      const draftAgent: DraftAgent | undefined = selectedAgent
        ? {
            id: selectedAgent._id || 'unknown',
            name: selectedAgent.name,
            siteUrl: selectedAgent.siteUrl,
            description: selectedAgent.description,
          }
        : undefined;

      // Save with consistent content
      const savedDraftId = await saveDraft(
        draftTitle ||
          title ||
          `Auto-saved Chat - ${new Date().toLocaleDateString()}`,
        draftMessages,
        documentContentForSaving,
        documentContentForSaving,
        titleRef.current,
        tocRef.current,
        draftAgent,
        draftDescription,
        currentDraftId || undefined
      );

      setCurrentDraftId(savedDraftId);
      updateLastSavedState();

      // Only show success message if not navigating (to avoid duplicate messages)
      if (!navigationState) {
        showSnackbar(
          currentDraftId
            ? 'Draft updated successfully'
            : 'Draft saved successfully',
          'success'
        );
        setSaveDraftDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      showSnackbar('Failed to save draft', 'error');
      // Reset navigation state on error
      setNavigationState(null);
      throw error; // Re-throw to be caught by the navigation handler
    } finally {
      setIsSavingDraft(false);
      // Emit saving state
      window.dispatchEvent(
        new CustomEvent('savingStateChange', { detail: { saving: false } })
      );
      if (!navigationState) {
        setSaveDraftDialogOpen(false);
      }
    }
  };

  // Function to handle sending a message
  const handleSendMessage = async () => {
    if (newMessage.trim() === '' || isLoading) return;

    // Add user message
    const userMessage: Message = {
      id: generateUniqueId(),
      text: newMessage,
      isUser: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);

    try {
      // Convert message history to format expected by API
      const messageHistory = messages.map((m) => ({
        id: m.id,
        text: m.text,
        isUser: m.isUser,
      }));

      // Add the user message
      messageHistory.push({
        id: userMessage.id,
        text: userMessage.text,
        isUser: true,
      });

      // Get AI response using current document state
      const currentDocumentContent = getDocumentContentForSaving();
      const aiResponseString = await generateResponse(
        messageHistory,
        currentDocumentContent,
        tocRef.current,
        titleRef.current,
        selectedAgent
      );

      // Parse the JSON response
      const aiResponse = JSON.parse(aiResponseString) as AIJsonResponse;

      // Process the response based on which agent handled it
      await handleAIResponse(aiResponse);
    } catch (error) {
      console.error('Error getting AI response:', error);

      // Show error message
      const errorMessage: Message = {
        id: generateUniqueId(),
        text: 'Sorry, there was an error processing your request. Please try again.',
        isUser: false,
        agentType: 'natural_agent',
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle AI response based on agent type and update flags
  const handleAIResponse = async (response: AIJsonResponse) => {
    console.log('AI Response:', response);

    // Update document title if changed
    if (response.flags.titleUpdated && response.title) {
      updateTitle(response.title);
      titleRef.current = response.title;
      setCurrentTitle(response.title);
    }

    // Update TOC if changed
    if (response.flags.tocUpdated && response.toc) {
      tocRef.current = response.toc;
      setCurrentToc(response.toc);

      // Dispatch an event to notify other components about the TOC update
      const tocUpdateEvent = new CustomEvent('ai_response', {
        detail: {
          response: {
            toc: response.toc,
            title: response.title,
          },
        },
      });
      window.dispatchEvent(tocUpdateEvent);

      // Open TOC dialog if TOC was specifically updated
      if (response.flags.activeAgent === 'toc_agent') {
        setTocDialogOpen(true);
      }
    }

    // Update document if changed
    if (response.flags.documentUpdated && response.document) {
      updateMarkdownContent(response.document);
    }

    // Add AI response message with appropriate type
    const aiMessage: Message = {
      id: generateUniqueId(),
      text: response.conversation,
      isUser: false,
      agentType: response.flags.activeAgent,
    };

    // Add document content if document was updated
    if (response.flags.documentUpdated) {
      aiMessage.documentContent = response.document;
      aiMessage.documentTitle = response.title;

      // Auto-expand document preview
      setExpandedDocs((prev) => ({
        ...prev,
        [aiMessage.id]: true,
      }));
    }

    // Add TOC if TOC was updated
    if (response.flags.tocUpdated) {
      aiMessage.tableOfContents = response.toc;
    }

    setMessages((prev) => [...prev, aiMessage]);
  };

  // Calculate textarea rows based on content
  const calculateRows = (text: string): number => {
    const lineCount = text.split('\n').length;
    return Math.min(Math.max(1, lineCount), 5); // Min 1 row, max 5 rows
  };

  // Toggle document preview expansion
  const toggleDocExpansion = (messageId: number) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  // Function to render markdown content as HTML
  const renderMarkdownPreview = (markdown: string) => {
    try {
      const html = markdownToHtml(markdown);
      return { __html: html };
    } catch (error) {
      console.error('Error rendering markdown:', error);
      return { __html: '<p>Error rendering markdown preview</p>' };
    }
  };

  // Handle updating document from a message
  const handleUpdateDocument = async (content: string, title?: string) => {
    if (!content) return;

    try {
      updateMarkdownContent(content);
      if (title) {
        updateTitle(title);
      }
    } catch (error) {
      console.error('Error updating document:', error);
    }
  };

  // TOC dialog functions
  const handleOpenTOCDialog = (toc: string) => {
    setCurrentToc(toc);
    setTocDialogOpen(true);
  };

  const handleOpenLatestToc = () => {
    if (tocRef.current) {
      setCurrentToc(tocRef.current);
      setTocDialogOpen(true);
    } else {
      // Look for the most recent TOC in messages
      const tocMessages = messages.filter((m) => m.tableOfContents);
      if (tocMessages.length > 0) {
        const latestToc = tocMessages[tocMessages.length - 1];
        setCurrentToc(latestToc.tableOfContents || '');
        setTocDialogOpen(true);
      }
    }
  };

  const handleCloseTocDialog = () => {
    setTocDialogOpen(false);
  };

  const handleUpdateWithToc = (newTocContent?: string) => {
    // If new content is provided (manual edit), use it
    if (newTocContent !== undefined) {
      tocRef.current = newTocContent;
      setCurrentToc(newTocContent);

      // Trigger change detection for auto-save
      debouncedChangeTracking();

      console.log(
        '📋 TOC manually updated:',
        newTocContent.length,
        'characters'
      );
      return true;
    }

    // Otherwise, use current TOC (legacy behavior)
    if (currentToc) {
      tocRef.current = currentToc;
      return true;
    }
    return false;
  };

  // Handle keyboard event for sending message
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current);
      }
    };
  }, []);

  // Add event listeners
  useEffect(() => {
    const handleEditorContentChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      // Only track changes if draft is loaded and change is from user
      if (draftLoadedRef.current && customEvent.detail.source === 'user') {
        debouncedChangeTracking();
      }
    };

    // Listen for editor content changes
    window.addEventListener(
      'editor_content_changed',
      handleEditorContentChange as EventListener
    );

    // Cleanup
    return () => {
      window.removeEventListener(
        'editor_content_changed',
        handleEditorContentChange as EventListener
      );
    };
  }, [debouncedChangeTracking]);

  // Track changes to determine if save button should be enabled - DEBOUNCED
  useEffect(() => {
    debouncedChangeTracking();
  }, [debouncedChangeTracking, messages]);

  if (isLoadingDraft)
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </div>
    );
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 1,
          borderBottom: 'none',
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="div">
            AI Document Assistant
          </Typography>
          {/* Show status chip whenever there are messages, not just for drafts */}
          {messages.length > 1 && (
            <Chip
              label={
                isSavingDraft
                  ? 'Saving...'
                  : hasUnsavedChanges
                    ? 'Auto-save pending'
                    : 'Draft saved'
              }
              size="small"
              color={
                isSavingDraft
                  ? 'primary'
                  : hasUnsavedChanges
                    ? 'warning'
                    : 'success'
              }
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: '20px' }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip
            title={
              currentDraftId
                ? hasUnsavedChanges
                  ? 'Save changes to draft'
                  : 'Draft saved'
                : 'Save as draft'
            }
          >
            <IconButton
              color="primary"
              onClick={handleOpenSaveDraftDialog}
              disabled={
                messages.length <= 1 ||
                isSavingDraft ||
                (!hasUnsavedChanges && !!currentDraftId)
              }
              size="medium"
              sx={{
                'bgcolor': currentDraftId
                  ? hasUnsavedChanges
                    ? 'warning.light'
                    : 'success.light'
                  : 'primary.light',
                'color': 'white',
                '&:hover': {
                  bgcolor: currentDraftId
                    ? hasUnsavedChanges
                      ? 'warning.main'
                      : 'success.main'
                    : 'primary.main',
                },
                '&.Mui-disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
                'width': 36,
                'height': 36,
              }}
            >
              {isSavingDraft ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          {/* <Tooltip title="View drafts">
            <IconButton
              color="primary"
              onClick={() => navigate('/drafts')}
              size="medium"
              sx={{
                'bgcolor': 'primary.light',
                'color': 'white',
                '&:hover': {
                  bgcolor: 'primary.main',
                },
                'width': 36,
                'height': 36,
              }}
            >
              <FolderOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip> */}

          <Tooltip title="View table of contents">
            <IconButton
              color="primary"
              onClick={handleOpenLatestToc}
              disabled={!tocRef.current}
              size="medium"
              sx={{
                'bgcolor': 'primary.light',
                'color': 'white',
                '&:hover': {
                  bgcolor: 'primary.main',
                },
                '&.Mui-disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
                'width': 36,
                'height': 36,
              }}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Show agent error if there's an issue */}
      {agentError && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {agentError}
        </Alert>
      )}

      {/* Show loading indicator while agent is loading */}
      {loadingAgent && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <List sx={{ flex: 1, overflow: 'auto', p: 1, pt: 0.5 }}>
          {messages.map((message) => (
            <ListItem
              key={message.id}
              sx={{
                flexDirection: 'column',
                alignItems: message.isUser ? 'flex-end' : 'flex-start',
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '95%',
                  width: '100%',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 500,
                    alignSelf: message.isUser ? 'flex-end' : 'flex-start',
                  }}
                >
                  {message.isUser
                    ? 'You'
                    : selectedAgent
                      ? selectedAgent.name
                      : 'Assistant'}
                </Typography>

                {/* Agent identifier for non-user messages */}
                {/* {!message.isUser && message.agentType && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Chip
                      icon={getAgentIcon(message.agentType)}
                      label={
                        message.agentType === 'natural_agent'
                          ? 'Natural Agent'
                          : message.agentType === 'toc_agent'
                            ? 'TOC Agent'
                            : message.agentType === 'document_agent'
                              ? 'Document Agent'
                              : 'AI Assistant'
                      }
                      size="small"
                      color={
                        message.agentType === 'natural_agent'
                          ? 'primary'
                          : message.agentType === 'toc_agent'
                            ? 'secondary'
                            : message.agentType === 'document_agent'
                              ? 'success'
                              : 'default'
                      }
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  </Box>
                )} */}

                {/* Table of Contents UI */}
                {message.tableOfContents && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Chip
                      icon={<FormatListBulletedIcon />}
                      label="Table of Contents"
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleOpenTOCDialog(message.tableOfContents!)
                      }
                      sx={{ p: 0.5 }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                {/* Document UI */}
                {message.documentContent && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Chip
                      icon={<EditNoteIcon />}
                      label={message.documentTitle || 'Document Updated'}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => toggleDocExpansion(message.id)}
                      sx={{ p: 0.5 }}
                    >
                      {expandedDocs[message.id] ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                )}

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    width: '100%',
                    bgcolor: message.isUser ? 'primary.light' : 'grey.100',
                    color: message.isUser ? 'white' : 'text.primary',
                  }}
                >
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.text}
                  </Typography>

                  {/* TOC view button */}
                  {message.tableOfContents && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FormatListBulletedIcon />}
                      onClick={() =>
                        handleOpenTOCDialog(message.tableOfContents!)
                      }
                      sx={{ mt: 1 }}
                    >
                      View Table of Contents
                    </Button>
                  )}
                </Paper>

                {/* Document preview section */}
                {message.documentContent && (
                  <Collapse in={expandedDocs[message.id] || false}>
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        maxHeight: '300px',
                        overflow: 'auto',
                      }}
                    >
                      <div
                        className="markdown-preview"
                        dangerouslySetInnerHTML={renderMarkdownPreview(
                          message.documentContent
                        )}
                      />

                      {/* Update button */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mt: 2,
                        }}
                      >
                        <Tooltip title="Update document with this content">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              updatingDocId === message.id ? (
                                <CircularProgress size={14} />
                              ) : (
                                <UpdateIcon />
                              )
                            }
                            onClick={() =>
                              handleUpdateDocument(
                                message.documentContent!,
                                message.documentTitle
                              )
                            }
                            disabled={updatingDocId !== null}
                            color="primary"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {updatingDocId === message.id
                              ? 'Updating...'
                              : 'Update Document'}
                          </Button>
                        </Tooltip>
                      </Box>
                    </Paper>
                  </Collapse>
                )}
              </Box>
            </ListItem>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                my: 2,
              }}
            >
              <CircularProgress size={24} />
              <Tooltip
                title="Der KI-Assistent arbeitet und analysiert Ihre Anfrage. Dies kann je nach Umfang mehrere Minuten dauern."
                placement="top"
                arrow
              >
                <InfoIcon
                  sx={{
                    color: 'primary.main',
                    fontSize: 16,
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            </Box>
          )}

          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </List>

        {/* Table of Contents Modal */}
        <TOCModal
          open={tocDialogOpen}
          onClose={handleCloseTocDialog}
          onUpdate={handleUpdateWithToc}
          tocContent={currentToc}
          title={currentTitle || title}
        />

        {/* Message input section */}
        <Divider />
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={calculateRows(newMessage)}
            maxRows={5}
            variant="outlined"
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                padding: '10px',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Save Draft Dialog */}
      <Dialog
        open={saveDraftDialogOpen}
        onClose={() => setSaveDraftDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Draft</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Draft Title"
            fullWidth
            variant="outlined"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.target.value)}
            placeholder="Add a brief description of this draft..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDraftDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveDraft}
            variant="contained"
            disabled={!draftTitle.trim() || isSavingDraft}
          >
            {isSavingDraft ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : currentDraftId ? (
              'Update Draft'
            ) : (
              'Save Draft'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <SnackbarContent
          sx={{
            backgroundColor:
              snackbar.severity === 'success'
                ? 'success.main'
                : snackbar.severity === 'error'
                  ? 'error.main'
                  : 'info.main',
          }}
          message={snackbar.message}
        />
      </Snackbar>
    </Box>
  );
};

export default ChatSection;
